import { captureMessage } from '@sentry/browser';
import size from 'lodash/size';
import type { ActionTree } from 'vuex';

import { HTTP_STATUS_CODES } from '@emobg/web-utils';

import { API_CHECKPOINTS } from '@/consts/checkpoints.const';
import { STEPS_NAMES, STEPS_VALIDATION } from '@/consts/steps.const';
import { splitPhoneNumber } from '@/utils/get-phone-prefix';
import type { UpdateInitialDataResponse } from '@api/api.model';
import { ApiService } from '@api/api.service';

import { AppActions } from '../App/actions';
import { AppGetters } from '../App/getters';
import { AppMutations } from '../App/mutations';
import { BrokerActions } from '../Broker/actions';
import { BrokerGetters } from '../Broker/getters';
import type { State } from '../interfaces/state';
import type { ActionAugments } from '../types/actions';
import { StepsGetters } from './getters';
import { StepsMutations } from './mutations';

export enum StepsActions {
  SetErrorMessage = 'Steps:SET_ERROR_MESSAGE',
  ClearErrorMessage = 'Steps:CLEAR_ERROR_MESSAGE',
  IncreaseCurrentStep = 'Steps:INCREASE_CURRENT_STEP',
  DecreaseCurrentStep = 'Steps:DECREASE_CURRENT_STEP',
  ValidateStep = 'Steps:VALIDATE_STEP',
  ValidateBooking = 'Steps:VALIDATE_BOOKING',
  ValidateCountry = 'Steps:VALIDATE_COUNTRY',
  DocumentsToValidate = 'Steps:DOCUMENTS_TO_VALIDATE',
  ValidateDocuments = 'Steps:VALIDATE_DOCUMENTS',
  ValidateAddress = 'Steps:VALIDATE_ADDRESS',
  GetOnfidoToken = 'Steps:GET_ONFIDO_TOKEN',
  GetAddress = 'Steps:GET_ADDRESS',
  FinishScanProcess = 'Steps:FINISH_SCAN_PROCESS',
}

export interface StepsActionsData {
  [StepsActions.SetErrorMessage]: (context: ActionAugments, errorBackend: string) => void;
  [StepsActions.ClearErrorMessage]: (context: ActionAugments) => void;
  [StepsActions.IncreaseCurrentStep]: (context: ActionAugments) => void;
  [StepsActions.DecreaseCurrentStep]: (context: ActionAugments) => void;
  [StepsActions.ValidateStep]: (context: ActionAugments) => void;
  [StepsActions.ValidateBooking]: (context: ActionAugments, bookingInfo: any) => Promise<boolean>;
  [StepsActions.ValidateCountry]: (context: ActionAugments) => void;
  [StepsActions.DocumentsToValidate]: (context: ActionAugments) => void;
  [StepsActions.ValidateDocuments]: (context: ActionAugments) => void;
  [StepsActions.ValidateAddress]: (context: ActionAugments) => Promise<boolean>;
  [StepsActions.GetOnfidoToken]: (context: ActionAugments) => Promise<void>;
  [StepsActions.GetAddress]: (context: ActionAugments) => Promise<void>;
  [StepsActions.FinishScanProcess]: (context: ActionAugments) => Promise<void>;
}

export const actions: ActionTree<State, State> & StepsActionsData = {
  [StepsActions.SetErrorMessage]({ commit }, errorBackend) {
    const errorPayload = {
      backend: errorBackend,
      translation: `errors.${errorBackend}`,
    } as const;

    commit(StepsMutations.SetError, errorPayload);
    commit(StepsMutations.SetDataLoading, false);
  },

  [StepsActions.ClearErrorMessage]({ commit }) {
    commit(StepsMutations.ClearError);
  },

  [StepsActions.IncreaseCurrentStep]({ commit }) {
    commit(StepsMutations.IncreaseCurrentStep);
    commit(StepsMutations.ClearError);
  },

  [StepsActions.DecreaseCurrentStep]({ commit }) {
    commit(StepsMutations.DecreaseCurrentStep);
    commit(StepsMutations.ClearError);
  },

  [StepsActions.ValidateStep]({ dispatch, commit, getters }) {
    const currentStep: keyof typeof STEPS_VALIDATION = getters[StepsGetters.GetCurrentStep];
    const actionToDispatch = STEPS_VALIDATION[currentStep] as keyof typeof StepsActions;
    commit(StepsMutations.SetDataLoading, true);
    dispatch(StepsActions[actionToDispatch]);
  },

  async [StepsActions.ValidateBooking]({ dispatch, commit, getters }, bookingDetails): Promise<boolean> {
    const { email, bookingId, bookingDate } = bookingDetails;

    commit(StepsMutations.SetBookingDetails, bookingDetails);
    const api = new ApiService();

    const request = {
      locale: getters[BrokerGetters.GetLocale],
      email,
      bookingId,
      bookingDate,
    } as const;

    commit(StepsMutations.SetDataLoading, true);

    const { result, data } = await api.getValidateBooking(request);
    if (data && !data.token) {
      result.statusCode = HTTP_STATUS_CODES.noContent;
      captureMessage(`api.getValidationBooking: ${result.statusCode}`, { level: 'warning' });
    }

    if (!result || result.statusCode !== HTTP_STATUS_CODES.ok) {
      dispatch(StepsActions.SetErrorMessage, result.statusMessage);
      return false;
    }

    const payload = {
      locale: getters[BrokerGetters.GetLocale],
      ...data,
    } as const;

    commit(AppMutations.SetBrokerData, payload);
    dispatch(StepsActions.IncreaseCurrentStep);
    commit(StepsMutations.SetDataLoading, false);

    return true;
  },

  [StepsActions.ValidateCountry]({ dispatch, commit, getters }) {
    const api = new ApiService();
    const headers = getters[AppGetters.GetHeaders];
    const { code: selectedCountryCode } = getters[BrokerGetters.GetSelectedCountry];

    const payload = {
      headers,
      selectedCountryCode,
    } as const;

    api.getUpdateInitialData(payload).then((response: UpdateInitialDataResponse) => {
      if (response.result.statusCode === HTTP_STATUS_CODES.ok) {
        dispatch(BrokerActions.SetData, response.initialData);

        size(response.initialData.documentsForValidation)
          ? dispatch(StepsActions.IncreaseCurrentStep)
          : commit(StepsMutations.SetCurrentStep, STEPS_NAMES.updateAddress);
      } else {
        dispatch(StepsActions.SetErrorMessage, response.result.statusMessage);
      }
    });
  },

  [StepsActions.DocumentsToValidate]({ commit }) {
    commit(StepsMutations.IncreaseCurrentStep);
    commit(StepsMutations.SetDataLoading, false);
  },

  async [StepsActions.ValidateDocuments]({ commit, getters }) {
    const api = new ApiService();

    const headers = getters[AppGetters.GetHeaders];

    const { applicantId, documentType, documents } = getters[StepsGetters.GetStepData](STEPS_NAMES.validateDocuments);

    const scannedDocument = {
      clientCountry: getters[BrokerGetters.GetSelectedCountry],
      applicantId,
      documentType,
      documents,
    } as const;

    const { checkId } = await api.postScannedDocument(headers, scannedDocument);
    commit(StepsMutations.AddDocumentCheckId, checkId);
  },

  async [StepsActions.ValidateAddress]({ getters }) {
    const api = new ApiService();

    const headers = getters[AppGetters.GetHeaders];

    const { prefix, telephone, address, cp, city, province, selectedCountry, additionalDriverEmail, countryOfBirth } = getters[StepsGetters.GetStepData](
      STEPS_NAMES.updateAddress,
    );

    const addressData = {
      telephone,
      prefix,
      cp,
      address,
      city,
      province,
      selectedCountry,
      additionalDriverEmail,
      countryOfBirth,
    } as const;

    const response = await api.putUpdateAddress(headers, addressData);
    return response.result.statusCode === HTTP_STATUS_CODES.ok;
  },
  async [StepsActions.GetOnfidoToken]({ commit, getters }) {
    const api = new ApiService();
    const { brokerId, token } = getters[AppGetters.GetBrokerData];

    const { token: onfidoToken, applicantId } = await api.postOnfidoData(brokerId, token);
    commit(StepsMutations.SetOnfidoToken, onfidoToken);
    commit(StepsMutations.SetApplicantId, applicantId);
  },
  async [StepsActions.GetAddress]({ commit, getters }) {
    const api = new ApiService();
    const headers = getters[AppGetters.GetHeaders];

    const { compulsoryFields } = await api.getAddress(headers);
    const { number: telephone, prefix } = splitPhoneNumber(compulsoryFields.telephone);
    commit(StepsMutations.SetAddressData, { ...compulsoryFields, prefix, telephone });
  },
  async [StepsActions.FinishScanProcess]({ dispatch, getters }) {
    const api = new ApiService();
    const headers = getters[AppGetters.GetHeaders];
    const { documentsChecksIds } = getters[StepsGetters.GetStepData](STEPS_NAMES.validateDocuments);

    const { status } = await api.putDocumentsChecksIds(headers, documentsChecksIds);

    const checkpoint = status === HTTP_STATUS_CODES.ok ? API_CHECKPOINTS.processCompleted : API_CHECKPOINTS.processError;
    dispatch(AppActions.SetCheckpoint, checkpoint);
  },
} as const;
